import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, ChevronDown, AlertCircle, MapPin, UploadCloud, Download, List, FileText, Search, X, Calendar, RefreshCw, Check } from 'lucide-react';
import { DateRangePicker } from '../components/DateRangePicker/DateRangePicker';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useLoading } from '../context/LoadingContext';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { resolveNativeReportGroups, hasForecastingGrant } from '../config/reportGroupAccess';
import { getChartOfAccounts } from '../utils/api';
import { getFinancialBudgets, createFinancialBudget, budgetCache, createBulkFinancialBudgets } from '../utils/budgetApi';
import { getFinancialForecasts } from '../utils/forecastApi';
import { getLocationsByOrgId } from '../utils/locationApi';
import BudgetTable from '../components/Budgeting/BudgetTable';
import PnLBudgetView from '../components/Budgeting/PnLBudgetView';
import ForecastTable from '../components/Budgeting/ForecastTable';
import BudgetEntryModal from '../components/Budgeting/BudgetEntryModal';
import BudgetImportModal from '../components/Budgeting/BudgetImportModal';
import AssumptionsPanel from '../components/Budgeting/AssumptionsPanel';
import LoadingScreen from '../components/LoadingScreen';

const FORECAST_DRIVER_EXPLANATIONS = {
  'Static Entry': 'Forecast value is manually entered for each month. If no value is entered, it may pull from the budget.',
  '$ / ORN': 'Forecast is calculated by multiplying a user-defined rate by the number of Occupied Room Nights for each month (e.g., $2.00/ORN * 500 ORN = $1,000).',
  '$ / ARN': 'Forecast is calculated by multiplying a user-defined rate by the number of Available Room Nights for each month.',
  'Total Guests': 'Forecast is calculated by multiplying a user-defined driver value by the number of guests for each month.',
  '$ / ARN LM': "Forecast is based on last month\'s actual calculated dollars per Available Room Night, applied to the current month\'s Available Room Nights.",
  '$ / ORN LM': "Forecast is based on last month\'s actual calculated dollars per Occupied Room Night, applied to the current month\'s Occupied Room Nights.",
  '$ LM': "Forecast uses the total actual booked amount from the previous month.",
  'Integrated Driver': 'Forecast data is automatically pulled from integrated external systems (e.g., labor management, revenue management systems). No manual entry is typically required.',
  'Ledger Integration': 'Forecast initially uses budgeted values. Once actual financial data is posted from an integrated ledger system for a period, the forecast for that period is replaced by the actuals.'
};

const Budgeting = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { startLoading, stopLoading } = useLoading();
  const budgetingEnabled = useFeatureFlagEnabled('budgeting');
  const { 
    currentOrganization, // The currently selected organization object { id, code, name, workspaceId, ... }
    userSettings,
    isLoading: isLoadingUserSettings // Loading state from the context hook
  } = useUserSettingsContext();
  // Explicit per-user grant (User setup → Report Access → Budgeting &
  // Forecasting) opens this page regardless of the PostHog flag.
  const forecastingGranted = hasForecastingGrant(resolveNativeReportGroups(currentOrganization, userSettings));
  const [activeTab, setActiveTab] = useState('Budget');
  // Date range state - defaults to current year
  const currentYear = new Date().getFullYear();
  const [dateRange, setDateRange] = useState({
    start: `${currentYear}-01-01`,
    end: `${currentYear}-12-31`
  });
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [forecastData, setForecastData] = useState([]);
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState('Monthly Averages');
  const [selectedMetric, setSelectedMetric] = useState('Cost Room Night');
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);
  const [showDisplayModeDropdown, setShowDisplayModeDropdown] = useState(false);
  const [budgetingError, setBudgetingError] = useState(null); // Local error state for budgeting actions
  const [isAssumptionsPanelOpen, setIsAssumptionsPanelOpen] = useState(false);
  const [assumptions, setAssumptions] = useState({});
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isForecastVsBudget, setIsForecastVsBudget] = useState(false); // New state for Forecast vs Budget toggle
  const [accountTypeFilter, setAccountTypeFilter] = useState('All'); // Filter for account type
  const [showAccountTypeDropdown, setShowAccountTypeDropdown] = useState(false);
  const [showDriverExplanationsModal, setShowDriverExplanationsModal] = useState(false);
  const [forecastViewMode, setForecastViewMode] = useState('pnl'); // 'list' or 'pnl' for Forecast tab
  const [forecastSearchTerm, setForecastSearchTerm] = useState(''); // Search term for Forecast Account List
  const [isImportModalOpen, setIsImportModalOpen] = useState(false); // State for import modal

  // Add loading tracking states
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Refs
  const dataLoadedRef = useRef(false);
  const isMountedRef = useRef(true);
  const isUpdatingRef = useRef(false);
  const lastAccountsRequestRef = useRef({ orgId: null, locationId: null }); // Track last request params
  const fileInputRef = useRef(null);
  const dateRangeInitializedRef = useRef(false); // Track if date range was restored from URL
  const locationInitializedRef = useRef(false); // Track if location was initialized from URL or default

  // Derive year from date range for backwards compatibility
  const selectedYear = useMemo(() => {
    if (!dateRange.start) return currentYear;
    return parseInt(dateRange.start.split('-')[0], 10);
  }, [dateRange.start, currentYear]);

  // Derive start/end months from date range (0-indexed)
  const dateRangeMonths = useMemo(() => {
    if (!dateRange.start || !dateRange.end) {
      return { startMonth: 0, endMonth: 11 };
    }
    const [startYear, startMonthStr] = dateRange.start.split('-');
    const [endYear, endMonthStr] = dateRange.end.split('-');

    // If years are different, default to full year of start date
    if (startYear !== endYear) {
      return { startMonth: 0, endMonth: 11 };
    }

    const startMonth = parseInt(startMonthStr, 10) - 1;
    const endMonth = parseInt(endMonthStr, 10) - 1;

    // Validate months are in valid range
    if (startMonth < 0 || startMonth > 11 || endMonth < 0 || endMonth > 11) {
      return { startMonth: 0, endMonth: 11 };
    }

    // Ensure start <= end
    if (startMonth > endMonth) {
      return { startMonth: endMonth, endMonth: startMonth };
    }

    return { startMonth, endMonth };
  }, [dateRange]);

  // Define months for date calculations
  const months = useMemo(() => [
    { key: '0', label: 'Jan' },
    { key: '01', label: 'Feb' },
    { key: '02', label: 'Mar' },
    { key: '03', label: 'Apr' },
    { key: '04', label: 'May' },
    { key: '05', label: 'Jun' },
    { key: '06', label: 'Jul' },
    { key: '07', label: 'Aug' },
    { key: '08', label: 'Sep' },
    { key: '09', label: 'Oct' },
    { key: '10', label: 'Nov' },
    { key: '11', label: 'Dec' }
  ], []);

  // Years for dropdown - defined early so it can be used in useEffect
  const years = [2022, 2023, 2024, 2025, 2026];

  // Account types for filtering
  const accountTypes = ['All', 'Revenue', 'Expense', 'Statistics', 'Asset', 'Liability', 'Equity'];

  // Filter chart of accounts based on selected account type
  const filteredChartOfAccounts = useMemo(() => {
    if (accountTypeFilter === 'All') {
      return chartOfAccounts;
    }
    // Handle Statistics filter to match both "Statistics" and "Statistical" account types
    if (accountTypeFilter === 'Statistics') {
      return chartOfAccounts.filter(account =>
        account.accountType === 'Statistics' || account.accountType === 'Statistical'
      );
    }
    return chartOfAccounts.filter(account => account.accountType === accountTypeFilter);
  }, [chartOfAccounts, accountTypeFilter]);

  // Effect to clear location/budget data if organization changes to null
  useEffect(() => {
    if (!currentOrganization) {
       setLocations([]);
       setSelectedLocation(null);
       setBudgetData([]);
       setChartOfAccounts([]); // Clear accounts if org changes
    }
    // Reset selected location if the current one doesn't belong to the new org
    if (currentOrganization && selectedLocation && selectedLocation.orgId !== currentOrganization.id) {

       setSelectedLocation(null); // Will trigger location/budget fetch or show 'select location'
    }
    
    // Setup mounted ref
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, [currentOrganization]);

  // Redirect if feature flag is disabled
  useEffect(() => {
    if (budgetingEnabled === false && !forecastingGranted) {
      navigate('/dashboard');
    }
  }, [budgetingEnabled, forecastingGranted, navigate]);

  // Load initial data: locations (after organization is loaded)
  useEffect(() => {
    // Only run if the organization has been loaded from context
    // Also skip if user settings are still loading
    if (!currentOrganization || isLoadingUserSettings || !isMountedRef.current) {

       return;
    }

    const fetchInitialData = async () => {
      if (!isMountedRef.current) return;
      
      setBudgetingError(null); // Clear previous budgeting errors

      try {

        // Fetch locations for this org using the organization ID
        try {
          // Ensure we have an ID to fetch locations
          if (!currentOrganization.id) {
             console.error('Organization object from context does not contain an ID:', currentOrganization);
             throw new Error('Current Organization ID is missing, cannot fetch locations.');
          }
          
          const locationsData = await getLocationsByOrgId(currentOrganization.id);
          if (isMountedRef.current) {
             if (locationsData && Array.isArray(locationsData) && locationsData.length > 0) {

               setLocations(locationsData);

               // Smart default location selection
               // Note: URL parameter restoration is handled by a separate useEffect
               // 1. Try to keep the previously selected location if it belongs to the new org
               const previousLocationStillValid = selectedLocation && locationsData.some(loc => loc.id === selectedLocation.id);
               if (previousLocationStillValid) {

               } else {
                  // 2. Otherwise, set default selected location to the first one in the new list
                  // This may be overridden by URL parameters in the restoration useEffect

                  setSelectedLocation(locationsData[0]);
               }
             } else {
               console.warn('No locations found or invalid locations data structure for org ID:', currentOrganization.id);
               setLocations([]);
               setSelectedLocation(null); // Ensure no location is selected if none are found
               setBudgetingError('No locations found for this organization.');
             }
          }
        } catch (locationError) {
          console.error('Error fetching locations:', locationError);
          if (isMountedRef.current) {
            setLocations([]);
            setSelectedLocation(null);
            setBudgetingError(`Failed to load locations: ${locationError.message}`);
          }
        }

        // NOTE: Chart of accounts loading is now handled in a separate effect
        // Load saved assumptions from localStorage
        try {
          const savedAssumptions = localStorage.getItem('budgetAssumptions');
          // Only parse if component is still mounted
          if (isMountedRef.current && savedAssumptions) { 
            setAssumptions(JSON.parse(savedAssumptions));
          }
        } catch (assumptionsError) {
          console.error('Error parsing saved assumptions:', assumptionsError);
        }

      } catch (err) {
        console.error('Error loading initial data:', err);
        if (isMountedRef.current) {
          setBudgetingError('Failed to load initial budgeting data. Please try refreshing.');
        }
      }
    };

    fetchInitialData();

  }, [currentOrganization, isLoadingUserSettings]);

  // Optimized effect to load chart of accounts when organization or location changes
  useEffect(() => {
    // Skip if component unmounted or org not ready
    if (!currentOrganization?.id || !isMountedRef.current) {
      return;
    }

    // Check if there's a locationId in the URL params
    const locationIdParam = searchParams.get('locationId');

    // If there's a locationId in URL, wait until location is initialized from URL params
    // This prevents fetching all accounts before the URL location is restored
    if (locationIdParam && !locationInitializedRef.current) {
      return;
    }

    // Skip if already loading the same data
    if (isLoadingAccounts) {
      return;
    }

    // Get the locationId if available, otherwise null for all accounts
    const locationId = selectedLocation?.id || null;
    
    // Check if we already loaded this exact combination to prevent duplicate calls
    const requestParams = { orgId: currentOrganization.id, locationId };
    if (lastAccountsRequestRef.current.orgId === requestParams.orgId &&
        lastAccountsRequestRef.current.locationId === requestParams.locationId) {
      return;
    }

    const fetchChartOfAccounts = async () => {
      try {
        setIsLoadingAccounts(true);
        setBudgetingError(null);

        // Store the current request parameters
        lastAccountsRequestRef.current = requestParams;

        // Pass both orgId and locationId for location-specific filtering
        const accounts = await getChartOfAccounts(currentOrganization.id, locationId);

        if (isMountedRef.current) {
          if (accounts && Array.isArray(accounts)) {
            setChartOfAccounts(accounts);
          } else {
            console.warn('Invalid chart of accounts data structure');
            setChartOfAccounts([]);
          }
        }
      } catch (accountsError) {
        console.error('Error fetching chart of accounts:', accountsError);
        if (isMountedRef.current) {
          setChartOfAccounts([]);
          setBudgetingError('Failed to load chart of accounts.');
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingAccounts(false);
        }
      }
    };

    fetchChartOfAccounts();
  }, [currentOrganization, selectedLocation]); // Re-run when org or location changes

  // Load budget data when year, selected location, or active tab changes  
  // Budget data is needed for both Budget tab and Forecast tab (for Budget driver type)
  useEffect(() => {
    // Skip if no location selected, no org, or component unmounted
    // Also check if org settings are loading
    if (!selectedLocation || !currentOrganization || !isMountedRef.current || isLoadingUserSettings) {

       setBudgetData([]); // Clear data if no location/org
       return;
    }

    // Skip if we're already updating
    if (isUpdatingRef.current) {

       return;
    }
    
    const fetchBudgetData = async () => {
      try {
        setBudgetingError(null); // Clear previous errors
        isUpdatingRef.current = true;

        // Clear cache to force a fresh fetch from the API
        if (budgetCache && typeof budgetCache.invalidateCache === 'function') {
          budgetCache.invalidateCache();
        }

        // Pass all filter parameters to the API for server-side filtering
        const budgets = await getFinancialBudgets({
          orgId: currentOrganization.id,
          orgCode: currentOrganization.code, // Provide both ID and code for flexibility
          locationId: selectedLocation.id,
          year: selectedYear
        });

        if (isMountedRef.current) {

          // Server-side filtering should handle most cases, but do a final check client-side just in case
          const filteredBudgets = budgets

          setBudgetData(filteredBudgets);
          dataLoadedRef.current = true; // Mark data as loaded *after* setting it
        }
      } catch (err) {
        console.error('Error fetching budget data:', err);
        if (isMountedRef.current) {
          setBudgetingError('Failed to load budget data. Please try again.');
          setBudgetData([]); // Clear data on error
        }
      } finally {
        if (isMountedRef.current) {
          isUpdatingRef.current = false;
        }
      }
    };

    fetchBudgetData();

  }, [selectedYear, selectedLocation, currentOrganization, isLoadingUserSettings, refreshKey]); // Remove start/stopLoading from dependencies

  // Restore year and location from URL params
  // This runs after locations are loaded and whenever URL changes
  useEffect(() => {
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const yearParam = searchParams.get('year'); // Legacy support
    const locationIdParam = searchParams.get('locationId');

    // Restore date range from URL - only on initial load
    if (!dateRangeInitializedRef.current) {
      if (startDateParam && endDateParam) {
        // New date range params
        dateRangeInitializedRef.current = true;
        setDateRange({
          start: startDateParam,
          end: endDateParam
        });
      } else if (yearParam) {
        // Legacy year param - convert to full year range
        const parsedYear = parseInt(yearParam, 10);
        if (!isNaN(parsedYear) && years.includes(parsedYear)) {
          dateRangeInitializedRef.current = true;
          setDateRange({
            start: `${parsedYear}-01-01`,
            end: `${parsedYear}-12-31`
          });
        }
      }
    }

    // Restore location - only if locations have been loaded
    if (locations.length > 0) {
      if (locationIdParam) {
        // Parse as integer since URL params are strings but IDs are numbers
        const locationIdNum = parseInt(locationIdParam, 10);
        const locationFromUrl = locations.find(loc => loc.id === locationIdNum);
        if (locationFromUrl && (!selectedLocation || selectedLocation.id !== locationFromUrl.id)) {
          setSelectedLocation(locationFromUrl);
        } else if (!locationFromUrl) {
          // Location from URL not found, use first location as default
          if (!selectedLocation && locations[0]) {
            setSelectedLocation(locations[0]);
          }
        }
      }
      // Mark location as initialized once locations are loaded and we've processed URL params
      locationInitializedRef.current = true;
    }
  }, [searchParams, years, locations]);

  // Restore active tab from URL params
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && (tabParam === 'Budget' || tabParam === 'Forecast')) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Load forecast data when year, selected location, or active tab changes
  useEffect(() => {
    // Skip if not on Forecast tab, no location selected, no org, or component unmounted
    if (activeTab !== 'Forecast' || !selectedLocation || !currentOrganization || !isMountedRef.current || isLoadingUserSettings) {

       if (activeTab !== 'Forecast') {
         setForecastData([]); // Clear forecast data when not on forecast tab
       }
       return;
    }

    // Initialize with empty forecast data immediately to show the table
    setForecastData([]);

    const fetchForecastData = async () => {
      try {
        setBudgetingError(null); // Clear previous errors

        // Fetch forecast data from financialForecast table
        const forecasts = await getFinancialForecasts({
          orgId: currentOrganization.id,
          locationId: selectedLocation.id,
          year: selectedYear
        });

        if (isMountedRef.current) {

          setForecastData(forecasts || []);
        }
      } catch (err) {
        console.error('Error fetching forecast data:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          url: err.config?.url
        });

        if (isMountedRef.current) {
          // Provide more specific error message based on error type
          let errorMessage = 'Failed to load existing forecast data, but you can still create new forecasts.';
          if (err.response?.status === 404) {
            // 404 means the endpoint or table doesn't exist yet - this is OK for new deployments

            errorMessage = null; // Don't show error for 404, just proceed with empty data
          } else if (err.response?.status >= 500) {
            errorMessage = 'Server error loading forecast data. You can still create new forecasts.';
          } else if (err.message === 'Network Error') {
            errorMessage = 'Network error loading forecast data. Check your connection and try refreshing.';
          }

          // Only set error message if it's not a 404 (which is expected for new setups)
          if (errorMessage) {
            setBudgetingError(errorMessage);
          }
          setForecastData([]); // Always clear data on error, but allow empty table to work
        }
      }
    };

    fetchForecastData();
  }, [activeTab, selectedYear, selectedLocation, currentOrganization, isLoadingUserSettings, refreshKey]);

  // Save budget data when it changes (placeholder for potential future use)
  const handleBudgetDataChange = (updatedRows, isTabNavigation = false) => {
    if (isTabNavigation) return;
    dataLoadedRef.current = true;
    // 
    // Potentially update local state if needed, but saving is handled by BudgetTable now
    // setBudgetData(updatedRows); // Be careful - this might conflict with the fetchBudgetData effect
  };

  // Save new budget entry
  const handleSaveBudgetEntry = async (entry) => {
    // Ensure we have organization and location context
    if (!currentOrganization || !selectedLocation) {
       setBudgetingError('Cannot save entry: Organization or Location data is missing.');
       return;
    }
      
    try {
      setBudgetingError(null); // Clear previous errors

      // Log the entry received from BudgetEntryModal

      // Find the chart of account ID for the given account number
      let chartOfAccountId = entry.chartOfAccountId;
      
      if (!chartOfAccountId) {

        // Try to match by accountNumber
        if (entry.accountNumber) {
          const matchingAccount = chartOfAccounts.find(acc => acc.accountNumber === entry.accountNumber);
          if (matchingAccount) {
            chartOfAccountId = matchingAccount.id;

          }
        }
        
        // If still not found, try matching by budgetCategory
        if (!chartOfAccountId && entry.budgetCategory) {
          const matchingAccountByCategory = chartOfAccounts.find(acc => acc.accountNumber === entry.budgetCategory);
          if (matchingAccountByCategory) {
            chartOfAccountId = matchingAccountByCategory.id;

          }
        }
        
        // Notify if still not found
        if (!chartOfAccountId) {
          console.warn('Could not find a matching chart of account for:', {
            accountNumber: entry.accountNumber,
            budgetCategory: entry.budgetCategory
          });
        }
      } else {

      }

      // Add orgCode, locationId and chartOfAccountId to the entry
      const entryToSave = {
          ...entry,
          orgCode: currentOrganization.code,
          locationId: selectedLocation.id,
          chartOfAccountId,
          inputCurrency: selectedLocation.currency || 'USD',
          inputAmount: entry.budgetAmount,
          // Ensure these key fields are never null
          budgetType: entry.budgetType || 'Expense', // Default to Expense if not set
          budgetCategory: entry.budgetCategory || entry.accountNumber || '',
          budgetDescription: entry.budgetDescription || ''
      };
      
      // Final validation before sending to API
      if (!entryToSave.accountNumber && !entryToSave.chartOfAccountId) {
        throw new Error('Budget entry must have either an account number or a chart of account ID');
      }

      const result = await createFinancialBudget(entryToSave); // Pass the complete object

      // Refresh budget data after adding new entry
      // Trigger the useEffect hook by invalidating cache and possibly fetching again
      // A simpler way is to just add the new entry to the existing state if the API returns it
      if (result && isMountedRef.current) {

        // Add the new entry to the budgetData state
        setBudgetData(prevData => [...prevData, result]);
      } else {
         // If result is not returned, force a refresh (less efficient)

         budgetCache.invalidateCache();
         // TODO: Consider triggering fetchBudgetData effect or adding refreshKey state
      }
      
      setIsEntryModalOpen(false);

    } catch (err) {
      console.error('Error saving budget entry:', err);

      // Improved error message parsing
      let errorMessage = 'Failed to save budget entry. Please try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message; // Use server error message if available
      } else if (err.message) {
         // Check for specific client-side or network errors
        if (err.message.includes('NULL into column')) {
           // Simplified handling for DB errors
           const columnMatch = err.message.match(/column '([^']+)'/);
           if (columnMatch && columnMatch[1]) {
              const fieldName = columnMatch[1].replace(/([A-Z])/g, ' $1').toLowerCase();
              errorMessage = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required. Please check your input.`;
           } else {
              errorMessage = 'A required field is missing. Please check your input.';
           }
        } else {
           errorMessage = err.message; // Use the generic error message
        }
      }

      if (isMountedRef.current) {
        setBudgetingError(errorMessage);
      }
    }
  };

  // Handle changes to assumptions
  const handleAssumptionsChange = (newAssumptions) => {
    setAssumptions(newAssumptions);
    localStorage.setItem('budgetAssumptions', JSON.stringify(newAssumptions));
    // Optionally trigger recalculation in BudgetTable if assumptions affect displayed values
  };

  // Handle refresh - re-fetch data without changing filters
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    setRefreshSuccess(false);
    setBudgetingError(null);

    // Invalidate cache to force fresh data
    if (budgetCache && typeof budgetCache.invalidateCache === 'function') {
      budgetCache.invalidateCache();
    }

    // Increment refreshKey to trigger useEffects
    setRefreshKey(prev => prev + 1);
  }, [isRefreshing]);

  // Callback when refresh completes (called from child components)
  const handleRefreshComplete = useCallback(() => {
    setIsRefreshing(false);
    setRefreshSuccess(true);
    // Clear success state after animation
    setTimeout(() => {
      setRefreshSuccess(false);
    }, 1500);
  }, []);

  // Display modes for dropdown
  const displayModes = [
    'Monthly Averages',
    'Annual Totals',
    'Year-over-Year',
    'Quarterly'
  ];

  // Metrics for display
  const metrics = [
    'Cost Room Night',
    'Revenue per Room',
    'Occupancy Rate',
    'Average Daily Rate'
  ];

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!currentOrganization || !selectedLocation) {
      setBudgetingError("Please select an organization and location before uploading a CSV.");
      return;
    }

    setIsUploadingCsv(true);
    setBudgetingError(null);

    try {
      const fileContent = await file.text();
      // 

      // Call a new function to parse and process the CSV data
      await processAndSaveCsvData(fileContent);

      setBudgetingError(null); // Clear any previous error
      // Optionally, show a success message or trigger a data refresh
      alert("CSV data uploaded and processed successfully!"); // Placeholder for better notification

      // Refresh budget data
      if (budgetCache && typeof budgetCache.invalidateCache === 'function') {
        budgetCache.invalidateCache();
      }
      // Trigger re-fetch by slightly changing a dependency of the budget fetch useEffect
      // This is a common pattern, or you could call a dedicated refresh function.
      // For simplicity, we'll rely on cache invalidation and existing effect triggers.
      // If direct re-fetch is needed:
      // fetchBudgetData(); // This might need to be wrapped or its dependencies managed carefully

    } catch (error) {
      console.error("Error processing CSV file:", error);
      setBudgetingError(`Error processing CSV: ${error.message}`);
    } finally {
      setIsUploadingCsv(false);
      // Reset file input to allow uploading the same file again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const triggerCsvUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handler for the BudgetImportModal
  const handleImportData = async (content, delimiter = ',') => {
    if (!currentOrganization || !selectedLocation) {
      throw new Error("Please select an organization and location before importing.");
    }

    setIsUploadingCsv(true);
    setBudgetingError(null);

    try {
      await processAndSaveCsvData(content, delimiter);

      // Refresh budget data
      if (budgetCache && typeof budgetCache.invalidateCache === 'function') {
        budgetCache.invalidateCache();
      }

      // Increment refresh key to trigger data reload
      setRefreshKey(prev => prev + 1);

      alert("Budget data imported successfully!");
    } catch (error) {
      console.error("Error importing data:", error);
      throw error; // Re-throw so the modal can display the error
    } finally {
      setIsUploadingCsv(false);
    }
  };

  const processAndSaveCsvData = async (csvContent, delimiter = ',') => {
    if (!currentOrganization || !selectedLocation) {
      throw new Error("Organization or Location not selected.");
    }

    const lines = csvContent.split(/\r?\n/).map(line => line.trim()).filter(line => line);
    if (lines.length < 2) {
      throw new Error("CSV file must contain at least a header and one data row.");
    }

    const header = lines[0].split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
    const dataRows = lines.slice(1);
  
    // 
    // 
  
    const accountHeaderIndex = header.findIndex(h => h.toLowerCase() === 'account');
    if (accountHeaderIndex === -1) {
      throw new Error("CSV must contain an 'Account' column.");
    }
  
    const monthHeaders = [];
    const yearRegex = new RegExp(`\\d{1,2}/\\d{1,2}/${selectedYear}`);
    
    header.forEach((h, index) => {
      if (index === accountHeaderIndex || h.toLowerCase() === 'total') return; // Skip Account and Total columns
      
      const dateParts = h.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (dateParts) {
        const month = parseInt(dateParts[1], 10); // Month is 1-indexed from CSV
        const day = parseInt(dateParts[2], 10); // Day from CSV (e.g., 1)
        const year = parseInt(dateParts[3], 10);
  
        if (year === selectedYear) {
          // Store month as 0-indexed for JavaScript Date objects, and original header for mapping
          monthHeaders.push({ csvHeader: h, monthIndex: month - 1, year: year, columnIndex: index });
        } else {
          console.warn(`Skipping column "${h}" as its year (${year}) does not match selected year (${selectedYear}).`);
        }
      } else {
        console.warn(`Skipping column "${h}" due to unrecognized date format.`);
      }
    });
  
    if (monthHeaders.length === 0) {
      throw new Error(`No valid month columns found for the selected year ${selectedYear}. Dates should be in M/D/YYYY or MM/DD/YYYY format.`);
    }
    // 
  
    const budgetEntriesToCreate = [];
  
    for (const row of dataRows) {
      const values = row.split(delimiter).map(v => v.trim().replace(/^"|"$/g, ''));
      const accountNumberStr = values[accountHeaderIndex];
      if (!accountNumberStr) {
        console.warn("Skipping row due to missing account number:", row);
        continue;
      }
  
      // Clean account number: remove .000 if present
      const accountNumber = accountNumberStr;
  
      const accountInCoa = chartOfAccounts.find(acc => acc.accountNumber === accountNumber);
      if (!accountInCoa) {
        console.warn(`Account number ${accountNumber} not found in Chart of Accounts. Skipping this row.`);
        continue;
      }
      const chartOfAccountId = accountInCoa.id;
  
      for (const monthHeader of monthHeaders) {
        const rawValue = values[monthHeader.columnIndex];
        if (rawValue === undefined || rawValue === null || rawValue.trim() === "") {
          // 
          continue; // Skip if no value for this month
        }
  
        // Parse currency: remove currency symbols, commas, and spaces while preserving sign/decimal.
        const numericValue = parseFloat(String(rawValue).replace(/[^\d.-]/g, ''));
  
        if (isNaN(numericValue)) {
          console.warn(`Invalid numeric value "${rawValue}" for account ${accountNumber}, month ${monthHeader.csvHeader}. Skipping this month entry.`);
          continue;
        }
        
        // Construct budgetDate (YYYY-MM-DD) using UTC to avoid timezone issues
        // Month is 0-indexed for Date.UTC, day is 1
        const budgetDate = new Date(Date.UTC(monthHeader.year, monthHeader.monthIndex, 1));
        const budgetDateStr = budgetDate.toISOString().split('T')[0];
  
        budgetEntriesToCreate.push({
          orgCode: currentOrganization.code,
          locationId: selectedLocation.id,
          chartOfAccountId: chartOfAccountId,
          accountNumber: accountNumber, // Include for reference, though chartOfAccountId is primary
          budgetDate: budgetDateStr,
          budgetAmount: numericValue,
          inputCurrency: selectedLocation.currency || 'USD',
          inputAmount: numericValue,
          calculationFormula: null, // Assuming direct values from CSV
        });
      }
    }
  
    // 
  
    if (budgetEntriesToCreate.length > 0) {
      await createBulkFinancialBudgets(budgetEntriesToCreate);

       // Invalidate cache and trigger re-fetch - this is important
      if (budgetCache && typeof budgetCache.invalidateCache === 'function') {
        budgetCache.invalidateCache(); // Invalidate before potential re-fetch
      }
      // Attempt to trigger a re-fetch of budget data by changing a dependency of the useEffect.
      // This is a bit of a hack. A more robust solution would be a dedicated refresh function
      // or ensuring the `getFinancialBudgets` call is re-triggered.
      // For now, we assume cache invalidation + existing year/location change effects will pick it up.
      // Consider adding a `refreshKey` state if issues persist.
      // Example: setRefreshKey(prev => prev + 1); 
      // and add refreshKey to the budget data useEffect dependencies.

      // Forcing a re-render that might trigger the budget data fetch useEffect:
      // This is a common pattern, but sometimes a more direct refresh is needed.
      setDateRange(prev => ({ ...prev })); // Re-setting to trigger effect
      // If the above doesn't reliably refresh, we need a more explicit refresh mechanism.
      
    } else {

      throw new Error("No valid budget entries could be parsed from the CSV. Please check the format and ensure account numbers match the Chart of Accounts and dates match the selected year.");
    }
  };

  const handleDownloadTemplate = () => {
    if (!selectedLocation || !chartOfAccounts.length) {
      setBudgetingError("Please select a location and ensure chart of accounts are loaded before downloading template.");
      return;
    }

    try {
      // Create CSV content with proper format matching the import expectations
      // Header: Account column + 12 month columns (M/1/YYYY format) + Total column
      const monthHeaders = [];
      for (let month = 1; month <= 12; month++) {
        monthHeaders.push(`${month}/1/${selectedYear}`);
      }

      const csvHeader = ['Account', ...monthHeaders, 'Total'].join(',');
      const csvRows = [csvHeader];

      // Group budget data by account number for easy lookup
      const budgetByAccount = {};
      budgetData.forEach(budget => {
        const account = chartOfAccounts.find(acc => acc.id === budget.chartOfAccountId);
        if (account) {
          const accountNumber = account.accountNumber;
          if (!budgetByAccount[accountNumber]) {
            budgetByAccount[accountNumber] = {};
          }

          // Parse budget date and extract month (1-indexed)
          const budgetDate = new Date(budget.budgetDate);
          const month = budgetDate.getMonth() + 1; // Convert 0-indexed to 1-indexed

          // Store budget amount for this month
          budgetByAccount[accountNumber][month] = budget.budgetAmount || 0;
        }
      });

      // Add a row for each account in the chart of accounts
      filteredChartOfAccounts.forEach(account => {
        const accountNumber = account.accountNumber;
        const accountBudgets = budgetByAccount[accountNumber] || {};

        // Build row: Account number, then 12 months of data, then total
        const monthlyValues = [];
        let yearTotal = 0;

        for (let month = 1; month <= 12; month++) {
          const value = accountBudgets[month] || 0;
          monthlyValues.push(value);
          yearTotal += value;
        }

        // Format values (empty if 0, otherwise formatted number)
        const formattedValues = monthlyValues.map(v => v === 0 ? '' : v.toString());
        const formattedTotal = yearTotal === 0 ? '' : yearTotal.toString();

        const row = [accountNumber, ...formattedValues, formattedTotal].join(',');
        csvRows.push(row);
      });

      // Create CSV blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `Budget_Template_${selectedLocation.name}_${selectedYear}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('Budget template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      setBudgetingError(`Error downloading template: ${error.message}`);
    }
  };

  // Show loading indicator if user settings (including org) are loading
  if (isLoadingUserSettings) {
    return <LoadingScreen message="Loading User Settings" />;
  }

  // Render nothing or a placeholder while checking the flag/redirecting
  if (!forecastingGranted && (budgetingEnabled === null || budgetingEnabled === false)) {
    return <div className="flex justify-center items-center h-full">Checking feature availability...</div>;
  }

  return (
    <div className="flex flex-col p-3 w-full h-full">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 flex flex-col h-full overflow-hidden">
        {/* Compact Header - Single Row */}
        <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 bg-gray-50/50">
          {/* Title */}
          <h1 className="text-base font-semibold text-gray-800 whitespace-nowrap">
            {currentOrganization ? currentOrganization.name : 'Budgeting'}
          </h1>

          {/* Budget/Forecast Tabs */}
          <div className="flex bg-white rounded-md border border-gray-200 p-0.5">
            <button
              className={`px-3 py-1 font-medium text-sm rounded transition-all ${
                activeTab === 'Budget'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => {
                setActiveTab('Budget');
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('tab', 'Budget');
                  return newParams;
                });
              }}
            >
              Budget
            </button>
            <button
              className={`px-3 py-1 font-medium text-sm rounded transition-all ${
                activeTab === 'Forecast'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => {
                setActiveTab('Forecast');
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('tab', 'Forecast');
                  return newParams;
                });
              }}
            >
              Forecast
            </button>
          </div>

          {/* View Mode Toggle - Only for Forecast tab */}
          {activeTab === 'Forecast' && (
            <>
              <div className="h-4 w-px bg-gray-300" />
              <div className="flex bg-gray-100 rounded p-0.5">
                <button
                  onClick={() => setForecastViewMode('list')}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all ${
                    forecastViewMode === 'list'
                      ? 'bg-white text-orange-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <List size={12} />
                  <span className="hidden md:inline">Accounts</span>
                </button>
                <button
                  onClick={() => setForecastViewMode('pnl')}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all ${
                    forecastViewMode === 'pnl'
                      ? 'bg-white text-orange-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FileText size={12} />
                  <span className="hidden md:inline">P&L</span>
                </button>
              </div>
            </>
          )}

          {/* Search - Only for Forecast Account List view */}
          {activeTab === 'Forecast' && forecastViewMode === 'list' && (
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search accounts..."
                value={forecastSearchTerm}
                onChange={(e) => setForecastSearchTerm(e.target.value)}
                className="w-full pl-7 pr-7 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              />
              {forecastSearchTerm && (
                <button
                  onClick={() => setForecastSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right: Compact Controls */}
          <div className="flex items-center gap-1.5">
            {/* Location Selector */}
            <div className="relative">
              <button
                className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 bg-white text-sm font-medium hover:border-gray-300 transition-colors"
                onClick={() => setShowLocationDropdown(!showLocationDropdown)}
              >
                <MapPin size={12} className="text-orange-500" />
                <span className="max-w-[120px] truncate text-xs">{selectedLocation ? selectedLocation.name : (locations.length > 0 ? 'Location' : 'None')}</span>
                <ChevronDown size={12} className="text-gray-400" />
              </button>
              {showLocationDropdown && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px] max-h-64 overflow-y-auto">
                  {locations.length > 0 ? locations.map(location => (
                    <button
                      key={location.id}
                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors ${selectedLocation?.id === location.id ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-700'}`}
                      onClick={() => {
                        setSelectedLocation(location);
                        setSearchParams(prev => {
                          const newParams = new URLSearchParams(prev);
                          newParams.set('locationId', location.id);
                          newParams.delete('accountId');
                          return newParams;
                        });
                        setShowLocationDropdown(false);
                      }}
                    >
                      {location.name}
                    </button>
                  )) : (
                     <div className="px-3 py-2 text-sm text-gray-500">No locations available</div>
                  )}
                </div>
              )}
            </div>

            {/* Date Range Picker */}
            <div className="budgeting-date-picker" style={{ '--date-picker-min-width': '180px' }}>
              <style>{`
                .budgeting-date-picker .date-range-picker-input {
                  min-width: 180px !important;
                  padding: 4px 10px !important;
                  font-size: 12px !important;
                  border-radius: 4px !important;
                }
                .budgeting-date-picker .date-range-picker-input .dropdown-arrow {
                  font-size: 8px !important;
                }
              `}</style>
              <DateRangePicker
                startDate={dateRange.start}
                endDate={dateRange.end}
                onDateChange={(newRange) => {
                  setDateRange(newRange);
                  // Update URL with the date range
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('startDate', newRange.start);
                    newParams.set('endDate', newRange.end);
                    newParams.delete('year'); // Remove old year param
                    return newParams;
                  });
                }}
                placeholder="Select dates"
              />
            </div>

            {/* Account Type Filter */}
            <div className="relative">
              <button
                className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white text-xs font-medium hover:border-gray-300 transition-colors"
                onClick={() => setShowAccountTypeDropdown(!showAccountTypeDropdown)}
              >
                {accountTypeFilter}
                <ChevronDown size={12} className="text-gray-400" />
              </button>
              {showAccountTypeDropdown && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {accountTypes.map(type => (
                    <button
                      key={type}
                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors ${accountTypeFilter === type ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-700'}`}
                      onClick={() => {
                        setAccountTypeFilter(type);
                        setShowAccountTypeDropdown(false);
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Forecast Drivers Info Button - Only show on Forecast tab */}
            {activeTab === 'Forecast' && (
              <button
                onClick={() => setShowDriverExplanationsModal(true)}
                className="flex items-center gap-1 px-2 py-1 rounded border border-orange-200 bg-orange-50 text-orange-600 text-xs font-medium hover:bg-orange-100 transition-colors"
                title="How Drivers Work"
              >
                <AlertCircle size={12} />
                <span className="hidden lg:inline">Drivers</span>
              </button>
            )}

            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-1 rounded transition-all duration-300 ${
                refreshSuccess
                  ? 'text-green-500 bg-green-50'
                  : isRefreshing
                    ? 'text-orange-500 bg-orange-50'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title="Refresh data"
            >
              {refreshSuccess ? (
                <Check size={16} className="animate-bounce" />
              ) : (
                <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
              )}
            </button>

            <button
              className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              title="Settings"
            >
              <Settings size={16} />
            </button>
          </div>
        </div>
        
        {/* Error Message */}
        {budgetingError && (
          <div className="mx-3 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700 text-sm">
            <AlertCircle size={16} className="mr-2 flex-shrink-0" />
            <span className="flex-1">{budgetingError}</span>
            <button
              className="ml-2 text-red-500 hover:text-red-700 text-xs font-medium"
              onClick={() => setBudgetingError(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* CSV Upload Progress Bar */}
        {isUploadingCsv && (
          <div className="mx-3 mt-2">
            <p className="text-xs text-gray-600 mb-1">Processing CSV file...</p>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-orange-500 h-1.5 rounded-full animate-pulse w-3/4"></div>
            </div>
          </div>
        )}

        {/* Main Content - Scrollable */}
        {activeTab === 'Budget' && (
          <div className="flex-1 overflow-hidden flex flex-col p-3">
            {/* Assumptions Panel - Inline */}
            {currentOrganization && (
              <AssumptionsPanel
                isOpen={isAssumptionsPanelOpen}
                onToggle={() => setIsAssumptionsPanelOpen(!isAssumptionsPanelOpen)}
                mode='budget'
                assumptions={assumptions || {}}
                onAssumptionChange={handleAssumptionsChange}
                months={months.map(month => month.label)}
              />
            )}

            {/* Compact Action Bar */}
            <div className="flex items-center justify-between mb-2 gap-3">
              {/* Left: View Controls */}
              <div className="flex items-center gap-2">
                {/* Display Mode Dropdown */}
                <div className="relative">
                  <button
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-sm font-medium hover:border-gray-300 transition-colors"
                    onClick={() => setShowDisplayModeDropdown(!showDisplayModeDropdown)}
                  >
                    {displayMode}
                    <ChevronDown size={14} className="text-gray-400" />
                  </button>
                  {showDisplayModeDropdown && (
                    <div className="absolute left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                      {displayModes.map(mode => (
                        <button
                          key={mode}
                          className={`block w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors ${displayMode === mode ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-700'}`}
                          onClick={() => {
                            setDisplayMode(mode);
                            setShowDisplayModeDropdown(false);
                          }}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Metric Dropdown */}
                <div className="relative">
                  <button
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-sm font-medium hover:border-gray-300 transition-colors"
                    onClick={() => setShowMetricDropdown(!showMetricDropdown)}
                  >
                    {selectedMetric}
                    <ChevronDown size={14} className="text-gray-400" />
                  </button>
                  {showMetricDropdown && (
                    <div className="absolute left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px]">
                      {metrics.map(metric => (
                        <button
                          key={metric}
                          className={`block w-full text-left px-3 py-2 text-sm hover:bg-orange-50 transition-colors ${selectedMetric === metric ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-700'}`}
                          onClick={() => {
                            setSelectedMetric(metric);
                            setShowMetricDropdown(false);
                          }}
                        >
                          {metric}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  className="px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  onClick={() => setIsAssumptionsPanelOpen(true)}
                >
                  Assumptions
                </button>
              </div>

              {/* Right: Action Buttons */}
              <div className="flex items-center gap-1.5">
                <button
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleDownloadTemplate}
                  disabled={!selectedLocation || !chartOfAccounts.length}
                  title="Download budget template"
                >
                  <Download size={14} />
                  <span className="hidden sm:inline">Template</span>
                </button>
                <button
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setIsImportModalOpen(true)}
                  disabled={isUploadingCsv || !selectedLocation}
                  title="Import budget data from CSV or Excel"
                >
                  <UploadCloud size={14} />
                  <span className="hidden sm:inline">{isUploadingCsv ? 'Importing...' : 'Import'}</span>
                </button>
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 transition-colors"
                  onClick={() => setIsEntryModalOpen(true)}
                >
                  + Add Entry
                </button>
              </div>
            </div>
            
            {/* Budget Table */}
            <div className="flex-1 overflow-hidden">
              {!currentOrganization ? (
                 <div className="flex items-center justify-center h-full">
                    <div className="text-center p-8 bg-gray-50 rounded-lg">
                       {/* Could add a loading indicator here */}
                       <h3 className="text-lg font-medium text-gray-700 mb-1">Organization Not Loaded</h3>
                       <p className="text-gray-500">Organization details could not be loaded from context.</p>
                    </div>
                 </div>
              ) : !selectedLocation ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-8 bg-gray-50 rounded-lg">
                    <MapPin size={24} className="mx-auto mb-2 text-orange-500" />
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Select a Location</h3>
                    <p className="text-gray-500">Please select a location to view and edit budget data</p>
                  </div>
                </div>
              ) : (
                <PnLBudgetView
                  budgetData={budgetData}
                  chartOfAccounts={filteredChartOfAccounts}
                  onDataChange={handleBudgetDataChange}
                  orgId={currentOrganization.id}
                  orgCode={currentOrganization.code}
                  locationId={selectedLocation.id}
                  currency={selectedLocation.currency || 'USD'}
                  year={selectedYear}
                  initialStartMonth={dateRangeMonths.startMonth}
                  initialEndMonth={dateRangeMonths.endMonth}
                />
              )}
            </div>
          </div>
        )}

        {activeTab === 'Forecast' && (
          <div className="flex-1 overflow-hidden flex flex-col p-3">
            {/* Forecast Table */}
            <div className="flex-1 overflow-hidden">
              {!currentOrganization ? (
                 <div className="flex items-center justify-center h-full">
                    <div className="text-center p-8 bg-gray-50 rounded-lg">
                       {/* Could add a loading indicator here */}
                       <h3 className="text-lg font-medium text-gray-700 mb-1">Organization Not Loaded</h3>
                       <p className="text-gray-500">Organization details could not be loaded from context.</p>
                    </div>
                 </div>
              ) : !selectedLocation ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-8 bg-gray-50 rounded-lg">
                    <MapPin size={24} className="mx-auto mb-2 text-orange-500" />
                    <h3 className="text-lg font-medium text-gray-700 mb-1">Select a Location</h3>
                    <p className="text-gray-500">Please select a location to view and edit forecast data</p>
                  </div>
                </div>
              ) : (
                <ForecastTable
                  budgetData={budgetData}
                  forecastData={forecastData}
                  chartOfAccounts={filteredChartOfAccounts}
                  onDataChange={handleBudgetDataChange}
                  assumptions={assumptions}
                  location={selectedLocation}
                  orgCode={currentOrganization.code}
                  year={selectedYear}
                  isForecastVsBudget={isForecastVsBudget}
                  viewMode={forecastViewMode}
                  searchTerm={forecastSearchTerm}
                  initialStartMonth={dateRangeMonths.startMonth}
                  initialEndMonth={dateRangeMonths.endMonth}
                  refreshKey={refreshKey}
                  isRefreshing={isRefreshing}
                  onRefreshComplete={handleRefreshComplete}
                />
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Budget Entry Modal */}
      {isEntryModalOpen && currentOrganization && selectedLocation && (
        <BudgetEntryModal
          isOpen={isEntryModalOpen}
          onClose={() => setIsEntryModalOpen(false)}
          chartOfAccounts={filteredChartOfAccounts || []}
          onSave={handleSaveBudgetEntry}
          location={selectedLocation} // Pass the full selected location object
          // orgCode is handled within handleSaveBudgetEntry using currentOrganization state
        />
      )}

      {/* Budget Import Modal */}
      {isImportModalOpen && currentOrganization && selectedLocation && (
        <BudgetImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportData}
          selectedYear={selectedYear}
          isUploading={isUploadingCsv}
        />
      )}

      {/* Forecast Driver Explanations Modal */}
      {showDriverExplanationsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDriverExplanationsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle size={20} className="text-orange-600" />
                <h2 className="text-xl font-bold text-gray-900">How Forecast Drivers Work</h2>
              </div>
              <button
                onClick={() => setShowDriverExplanationsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
              <div className="space-y-4">
                {Object.entries(FORECAST_DRIVER_EXPLANATIONS).map(([driver, explanation]) => (
                  <div key={driver} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <h3 className="font-semibold text-gray-900 mb-1">{driver}</h3>
                    <p className="text-sm text-gray-600">{explanation}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowDriverExplanationsModal(false)}
                className="px-4 py-2 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budgeting;
